import React, {useMemo} from "react";

interface ContainerProps {
    className?: string;
    children: React.ReactNode;
    direction?: "row" | "col";
    justify?: "flex-start" | "center" | "flex-end" | "space-between" | "space-around" | "space-evenly";
    align?: "stretch" | "center" | "flex-start" | "flex-end" | "baseline";
    alignSelf?: "auto" | "flex-start" | "flex-end" | "center" | "baseline" | "stretch";
    flex?: number; // Sets the flex property (e.g., 1 for flex: 1, 2 for flex: 2, etc.)
    gap?: number;
    margin?: number;
    vMargin?: number;
    hMargin?: number;
    mTop?: number;
    mBottom?: number;
    mLeft?: number;
    mRight?: number;
    padding?: number;
    pTop?: number;
    pBottom?: number;
    pLeft?: number;
    pRight?: number;
    pHorizontal?: number;
    pVertical?: number;
    reverseMobile?: boolean;
    reverseTablet?: boolean;
    widthPercent?: number;
    maxWidth?: number;
    heightPercent?: number;
    maxHeight?: number;
    borderRadius?: number;
    onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    style?: React.CSSProperties;
}

interface PageProps {
    className?: string;
    children: React.ReactNode;
    maxWidthPercent?: number;
    maxWidthEMs?: number;
    fullscreen?: boolean;
}

export const StandardPage: React.FC<PageProps> = ({
                                                      className = "",
                                                      children,
                                                      maxWidthPercent = null,
                                                      maxWidthEMs = null,
                                                      fullscreen = false
                                                  }) => {
    const additionalContentStyles: React.CSSProperties = {};
    const additionalPageStyles: React.CSSProperties = {};

    if (maxWidthPercent) {
        additionalContentStyles.maxWidth = `${maxWidthPercent}%`;
    } else if (maxWidthEMs) {
        additionalContentStyles.maxWidth = `${maxWidthEMs}em`;
    }
    additionalContentStyles.justifyContent = 'center'
    additionalContentStyles.alignItems = 'center'

    if (!fullscreen) {
        additionalPageStyles.padding = `2%`
    }
    additionalContentStyles.width = "100%"
    additionalPageStyles.overflow = "hidden"

    return (
        <div className={`flex`} style={additionalPageStyles}>
            <div className={`flex ${className}`} style={additionalContentStyles}>
                {children}
            </div>
        </div>
    );
};

const BaseFlex: React.FC<ContainerProps> = ({
                                                className = "",
                                                children,
                                                gap = 0,
                                                margin = 0,
                                                vMargin = 0,
                                                hMargin = 0,
                                                mTop = 0,
                                                mBottom = 0,
                                                mLeft = 0,
                                                mRight = 0,
                                                padding = 0,
                                                pTop = 0,
                                                pBottom = 0,
                                                pLeft = 0,
                                                pRight = 0,
                                                pHorizontal = 0,
                                                pVertical = 0,
                                                widthPercent = null,
                                                maxWidth = 0,
                                                heightPercent = null,
                                                maxHeight = 0,
                                                justify,
                                                align,
                                                alignSelf,
                                                flex,
                                                reverseMobile = false,
                                                reverseTablet = false,
                                                direction = 'row',
                                                borderRadius,
                                                onClick,
                                                style = {}
                                            }) => {
    const computedStyle: React.CSSProperties = {
        ...(gap && { gap: `${gap}em` }),
        ...(widthPercent && { width: `${widthPercent}%` }),
        ...(maxWidth && { maxWidth: `${maxWidth}em` }),
        ...(heightPercent && { height: `${heightPercent}%` }),
        ...(maxHeight && { maxHeight: `${maxHeight}em` }),
        ...(margin && {margin: `${margin}em`}),
        ...(vMargin && { marginTop: `${vMargin}em`, marginBottom: `${vMargin}em` }),
        ...(hMargin && { marginLeft: `${hMargin}em`, marginRight: `${hMargin}em` }),
        ...(mTop && { marginTop: `${mTop}em` }),
        ...(mBottom && { marginBottom: `${mBottom}em` }),
        ...(mLeft && { marginLeft: `${mLeft}em` }),
        ...(mRight && { marginRight: `${mRight}em` }),
        ...(padding && {padding: `${padding}em`}),
        ...(pTop && { paddingTop: `${pTop}em` }),
        ...(pBottom && { paddingBottom: `${pBottom}em` }),
        ...(pLeft && { paddingLeft: `${pLeft}em` }),
        ...(pRight && { paddingRight: `${pRight}em` }),
        ...(pHorizontal && { paddingLeft: `${pHorizontal}em`, paddingRight: `${pHorizontal}em` }),
        ...(pVertical && { paddingTop: `${pVertical}em`, paddingBottom: `${pVertical}em` }),
        ...(justify && { justifyContent: justify }),
        ...(align && { alignItems: align }),
        ...(alignSelf && { alignSelf: alignSelf }),
        ...(flex && { flex: flex }),
        ...(borderRadius && { borderRadius: `${borderRadius}em` }),
        ...style,
    };

    const directionClass =
        reverseMobile && reverseTablet
            ? direction === 'row'
                ? 'flex-col sm:flex-col md:flex-row'
                : 'flex-row sm:flex-row md:flex-col'
            : reverseMobile
                ? direction === 'row'
                    ? 'flex-col sm:flex-row'
                    : 'flex-row sm:flex-col'
                : reverseTablet
                    ? direction === 'row'
                        ? 'flex-row md:flex-col'
                        : 'flex-col md:flex-row'
                    : direction === 'row'
                        ? 'flex-row'
                        : 'flex-col';

    return (
        <div className={`flex ${directionClass} ${className}`} style={computedStyle} onClick={onClick}>
            {children}
        </div>
    );
};

export const FlexRow = ({ children, ...rest }: ContainerProps) => (
    <BaseFlex {...rest} direction="row">
        {children}
    </BaseFlex>
);

export const FlexCol = ({ children, ...rest }: ContainerProps) => (
    <BaseFlex {...rest} direction="col">
        {children}
    </BaseFlex>
);

interface GridProps {
    className?: string;
    children: React.ReactNode;
    columns?: number;
    mobileColumns?: number;
    tabletColumns?: number;
    useMinMax?: boolean;
    minColumnWidthEM?: number;
    maxColumnWidthFR?: number;
    gap?: number;
    rowGap?: number;
    columnGap?: number;
    justifyContent?: "flex-start" | "center" | "flex-end" | "space-between" | "space-around" | "space-evenly";
    justifyItems?: "start" | "center" | "end" | "stretch";
    vMargin?: number;
    hMargin?: number;
    mTop?: number;
    mBottom?: number;
    mLeft?: number;
    mRight?: number;
    pTop?: number;
    pBottom?: number;
    pLeft?: number;
    pRight?: number;
    pHorizontal?: number;
    pVertical?: number;
    widthPercent?: number;
    maxWidthEMs?: number;
    minWidthEMs?: number;
    fixedItemWidth?: boolean;
}

export const BaseGrid: React.FC<GridProps> = ({
                                                  className = "",
                                                  children,
                                                  columns = 2,
                                                  mobileColumns,
                                                  tabletColumns,
                                                  useMinMax = false,
                                                  minColumnWidthEM = 16,
                                                  maxColumnWidthFR = 1,
                                                  gap = 0,
                                                  rowGap,
                                                  columnGap,
                                                  justifyContent,
                                                  justifyItems,
                                                  vMargin = 0,
                                                  hMargin = 0,
                                                  mTop = 0,
                                                  mBottom = 0,
                                                  mLeft = 0,
                                                  mRight = 0,
                                                  pTop = 0,
                                                  pBottom = 0,
                                                  pLeft = 0,
                                                  pRight = 0,
                                                  pHorizontal = 0,
                                                  pVertical = 0,
                                                  widthPercent = null,
                                                  maxWidthEMs = null,
                                                  minWidthEMs = null,
                                                  fixedItemWidth = false
                                              }) => {
    const gridId = useMemo(() => `grid-${Math.random().toString(36).substring(2, 9)}`, []);

    const desktopColumns = fixedItemWidth
        ? `repeat(auto-fill, minmax(${minColumnWidthEM}em, max-content))`
        : useMinMax
            ? `repeat(auto-fill, minmax(${minColumnWidthEM}em, ${maxColumnWidthFR}fr))`
            : `repeat(${columns}, minmax(0, 1fr))`;

    const mobileColumnsValue = mobileColumns ?? columns;
    const mobileColumnsCSS = `repeat(${mobileColumnsValue}, minmax(0, 1fr))`;
    const tabletColumnsValue = tabletColumns ?? columns;
    const tabletColumnsCSS = `repeat(${tabletColumnsValue}, minmax(0, 1fr))`;

    const style: React.CSSProperties = {
        display: "grid",
        gridTemplateColumns: desktopColumns,
        ...(gap && { gap: `${gap}em` }),
        ...(rowGap && { rowGap: `${rowGap}em` }),
        ...(columnGap && { columnGap: `${columnGap}em` }),
        ...(justifyContent && { justifyContent }),
        ...(justifyItems && { justifyItems }),
        ...(widthPercent && { width: `${widthPercent}%` }),
        ...(maxWidthEMs && { maxWidth: `${maxWidthEMs}em` }),
        ...(minWidthEMs && { minWidth: `${minWidthEMs}em` }),
        ...(vMargin && { marginTop: `${vMargin}em`, marginBottom: `${vMargin}em` }),
        ...(hMargin && { marginLeft: `${hMargin}em`, marginRight: `${hMargin}em` }),
        ...(mTop && { marginTop: `${mTop}em` }),
        ...(mBottom && { marginBottom: `${mBottom}em` }),
        ...(mLeft && { marginLeft: `${mLeft}em` }),
        ...(mRight && { marginRight: `${mRight}em` }),
        ...(pTop && { paddingTop: `${pTop}em` }),
        ...(pBottom && { paddingBottom: `${pBottom}em` }),
        ...(pLeft && { paddingLeft: `${pLeft}em` }),
        ...(pRight && { paddingRight: `${pRight}em` }),
        ...(pHorizontal && { paddingLeft: `${pHorizontal}em`, paddingRight: `${pHorizontal}em` }),
        ...(pVertical && { paddingTop: `${pVertical}em`, paddingBottom: `${pVertical}em` }),
    };

    return (
        <>
            {(mobileColumns !== undefined || tabletColumns !== undefined) && (
                <style>
                    {`
            ${mobileColumns !== undefined ? `@media (max-width: 640px) { .${gridId} { grid-template-columns: ${mobileColumnsCSS} !important; } }` : ''}
            ${tabletColumns !== undefined ? `@media (max-width: 1024px) { .${gridId} { grid-template-columns: ${tabletColumnsCSS} !important; } }` : ''}
          `}
                </style>
            )}
            <div className={`${gridId} ${className}`} style={style}>
                {children}
            </div>
        </>
    );
};